import { resolveFormTenantId } from "../../utils/tenantContext";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { displayDateUS as formatUsDisplayDate, getLocalSystemDate, toIsoDate } from "../../utils/dateUtils";
import { mergeFormDataWithStatusExtras, applyIsoDatesToFields } from "../../utils/applicationFormPayload";
import { useApplicationFormLogic } from "../../hooks/useApplicationFormLogic";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import PropTypes from 'prop-types';
import { request } from "../../utils/api";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import EvaluationInput from "../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../EvaluationNew/components/InputDatePicker";
import ApplicationFormFooter from "./ApplicationFormFooter";
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";
import { CONTRACTOR_PAGES } from "./contractorConstants";
import { zodResolver } from "@hookform/resolvers/zod";
import { contractorServiceAgreementSchema } from "./application.validation";
import useCurrentUser from "../../hooks/useCurrentUser";

const ContractorServiceAgreement = ({
  data,
  contractorName = "",
  onClose
}) => {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const { companyName } = useCurrentUser()

  // Initialize form
  const methods = useForm({
    resolver: zodResolver(contractorServiceAgreementSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      contractorName: contractorName || "",
      agencyName: companyName,
      serviceAgreementDate: formatUsDisplayDate(getLocalSystemDate()),
      agencyPrincipalAddress: "",
      agencyEmail: "",
      agreementSubject: "",
      serviceCounties: "",
      effectiveDate: formatUsDisplayDate(getLocalSystemDate()),
      applicantSignature: "",
      applicantSignedAt: "",
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: "",
      applicantStatementAccepted: false,
      ...data,
    },
    mode: "onChange"
  });

  const { handleSubmit, watch, reset, getValues, register } = methods;
  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (formData, flag = 'send_to_office', additionalData = {}) => {
    // Ensure flag is a string. If second arg is an event, default to 'send_to_office'
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    setLoading(true);

    try {
      const merged = mergeFormDataWithStatusExtras(formData, additionalData);
      const signatureIso = applyIsoDatesToFields(merged, [
        "applicantSignedAt",
        "reviewedBySignedAt",
      ]);

      const serviceRates = [];
      Object.keys(merged).forEach((key) => {
        if (key.startsWith("rate_")) {
          const parts = key.split("_");
          if (parts.length >= 3) {
            const discipline = parts[1];
            const serviceType = parts.slice(2).join("_");
            const rateValue = Number.parseFloat(merged[key]);
            if (!Number.isNaN(rateValue)) {
              serviceRates.push({ discipline, serviceType, rate: rateValue });
            }
          }
        }
      });

      const formatToIso = (rawDate) => {
        if (!rawDate || typeof rawDate !== "string") return toIsoDate(rawDate);
        const mdyMatch = rawDate.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (mdyMatch) {
          const [, m, d, y] = mdyMatch;
          return `${y}-${m}-${d}T00:00:00.000Z`;
        }
        return toIsoDate(rawDate);
      };

      const payload = {
        ...merged,
        flag: actualFlag,
        ...signatureIso,
        agreementDate: formatToIso(merged.serviceAgreementDate),
        serviceAgreementDate: formatToIso(merged.serviceAgreementDate),
        geographicAreaDescription: merged.serviceCounties,
        startDate: formatToIso(merged.effectiveDate || ""),
        effectiveDate: formatToIso(merged.effectiveDate || ""),
        contractorResponsibilities: Array.isArray(merged.contractorResponsibilities)
          ? merged.contractorResponsibilities
          : (merged.contractorResponsibilities ? [merged.contractorResponsibilities] : []),
        supervisionMethods: Array.isArray(merged.supervisionMethods)
          ? merged.supervisionMethods
          : (merged.supervisionMethods ? [merged.supervisionMethods] : []),
        serviceRates: serviceRates,
        contractorAuthorizedOfficialName: merged.applicantSignature,
        contractorDateSigned:
          signatureIso.applicantSignedAt ?? toIsoDate(merged.applicantSignedAt),
        agencyAuthorizedOfficialName: merged.reviewedBySignature,
        agencyTitle: merged.agencySignatureTitle,
        agencyDateSigned:
          signatureIso.reviewedBySignedAt ?? toIsoDate(merged.reviewedBySignedAt),
      };

      // Handle To Be Corrected comments
      const finalComments =
        merged.comments || additionalData?.comments || formData?.comments || getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      let response;
      const contractorRecordId = merged.id ?? formData.id;
      if (contractorRecordId) {
        response = await request(`/contractors/${contractorRecordId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        response = await request('/contractors', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      if (response.status === 1) {
        toast.success(actualFlag === 'save' ? "Agreement saved successfully" : "Agreement submitted successfully");
        closeForm()
      } else {
        toast.error(response.message || "Failed to process agreement");
      }
    } catch (error) {
      console.error("Error submitting agreement:", error);
      toast.error(`Failed to process agreement: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const {
    targetUserId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    signerName,
    user: currentUserData,
    isReadOnly,
    actualFromId,
    closeForm,
    handleSave,
    handleStatusAction,
    isAdmin
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "contractorName",
    applicantSignatureField: "applicantSignature",
    applicantSignatureDateField: "applicantSignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  // Fetch existing agreement data on mount
  useEffect(() => {
    const fetchAgreement = async () => {
      if (!currentUserData) return;
      if (isAdmin && !targetUserId) return;

      try {
        if (!actualFromId && !targetUserId) {
          if (currentUserData && !isAdmin) {
            reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              contractorName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              agencyName: companyName || "Infinite Home Health, Inc.",
              serviceAgreementDate: formatUsDisplayDate(getLocalSystemDate()),
              effectiveDate: formatUsDisplayDate(currentUserData.hireDate || getLocalSystemDate())
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          response = await request(`/contractors/${actualFromId}?userId=${targetUserId}`);
        } else {
          response = await request(`/contractors/${actualFromId}`);
        }

        let agreement = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) agreement = response.result[0];
          } else if (response.result?.agreement) {
            agreement = response.result.agreement;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            agreement = response.result.items.find(item => item.userId === parseInt(targetUserId));
          }
        }

        if (agreement) {
          setCurrentStatus(agreement.status || "");
          const formRates = {};
          if (agreement.serviceRates) {
            agreement.serviceRates.forEach(r => {
              formRates[`rate_${r.discipline}_${r.serviceType}`] = r.rate;
            });
          }
          const agreementDate = agreement.agreementDate ? formatUsDisplayDate(agreement.agreementDate) : formatUsDisplayDate(getLocalSystemDate());
          const startDate = agreement.startDate ? formatUsDisplayDate(agreement.startDate) : formatUsDisplayDate(getLocalSystemDate());

          reset({
            ...agreement,
            serviceAgreementDate: agreementDate,
            serviceCounties: agreement.geographicAreaDescription || "",
            effectiveDate: startDate,
            ...formRates,
            ...getStandardizedSignatures(agreement, isAdmin),
            comments: agreement.comments || ""
          });
        } else if (currentUserData && !isAdmin) {
          reset({
            tenantId: resolveFormTenantId(currentUserData?.tenantId),
            userId: currentUserData.id,
            contractorName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
            agencyName: companyName || "Infinite Home Health, Inc.",
            serviceAgreementDate: formatUsDisplayDate(getLocalSystemDate()),
            effectiveDate: formatUsDisplayDate(currentUserData.hireDate || getLocalSystemDate())
          });
        }
      } catch (err) {
        console.error("Error fetching agreement data:", err);
      }
    };
    fetchAgreement();
  }, [targetUserId, reset, currentUserData, isAdmin, actualFromId, companyName]);

  const currentPageContent = CONTRACTOR_PAGES.find(p => p.id === page);

  const renderSection = (section, index) => {
    switch (section.type) {
      case "header_inputs":
      case "inputs_group":
        return (
          <div
            key={index}
            className="mb-8 bg-gray-50 border border-gray-100 rounded-xl p-6"
          >
            {section.content && (
              <h3 className="font-bold text-lg mb-4 text-gray-800 border-b border-gray-200 pb-2">
                {section.content}
              </h3>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {section.fields.map((field) => (
                field.type === "date" ? (
                  <InputDatePicker
                    formatSeparator="/"
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    isRequired={field.required}
                  />
                ) : (
                  <EvaluationInput
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    placeholder={field.placeholder}
                    className={field.className}
                    isRequired={field.required}
                  />
                )
              ))}
            </div>
          </div>
        );
      case "text_block":
        return (
          <div
            key={index}
            className="mb-6 bg-white border border-gray-100 rounded-lg p-5 shadow-sm text-sm leading-relaxed text-gray-700 whitespace-pre-wrap hover:shadow-md transition-shadow"
          >
            {section.title && (
              <h4 className="font-bold text-gray-900 mb-3 text-base">
                {section.title}
              </h4>
            )}
            <p>{section.content}</p>
          </div>
        );
      case "list":
        return (
          <div
            key={index}
            className="mb-6 pl-4 text-sm space-y-3 bg-gray-50/50 p-4 rounded-lg border-l-4 border-blue-500"
          >
            {section.items.map((item, i) => (
              <div key={`${index}-${i}`} className="flex gap-2">
                <span className="text-blue-500 font-bold">•</span>
                <p className="text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        );
      case "rates_table":
        return (
          <div
            key={index}
            className="mb-8 overflow-hidden rounded-xl border border-gray-200 shadow-sm"
          >
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h4 className="font-bold text-gray-800">{section.title}</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-center">
                <thead className="bg-gray-100 text-gray-700 font-semibold uppercase text-xs tracking-wider">
                  <tr>
                    <th className="p-4 text-left border-b border-gray-200">
                      Discipline
                    </th>
                    {section.columns.map((col) => (
                      <th
                        key={col.key}
                        className="p-4 border-b border-gray-200"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {section.disciplines.map((disc) => (
                    <tr
                      key={disc.key}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="p-4 font-medium text-left text-gray-900">
                        {disc.label}
                      </td>
                      {section.columns.map((col) => (
                        <td key={col.key} className="p-4">
                          <div className="flex items-center justify-center max-w-[120px] mx-auto bg-gray-50 rounded-lg border border-gray-200 px-3 py-1 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                            <span className="mr-2 text-gray-400 font-medium">
                              $
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              {...register(`rate_${disc.key}_${col.key}`)}
                              className="w-full bg-transparent outline-none text-right font-medium text-gray-900 placeholder-gray-300"
                              placeholder="0.00"
                              onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                            />
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="max-w-7xl mx-auto bg-white shadow-2xl rounded-2xl overflow-hidden my-10 border border-gray-100">
        <EvaluationFormHeader
          title="Contractor Service Agreement"
          subtitle={`Page ${page} of ${CONTRACTOR_PAGES.length}`}
        />

        <div className="p-8">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit((d) => onSubmit(d, 'send_to_office'))}>
              <CorrectionRequestAlert status={currentStatus} comments={watch("comments")} />
              <fieldset disabled={isReadOnly || loading} className="space-y-8">
                {/* Header Content */}
                <div className="mb-8 p-6 bg-blue-50 border border-blue-100 rounded-xl text-center">
                  <h2 className="text-lg font-bold text-blue-900 uppercase tracking-widest">
                    Service Agreement
                  </h2>
                  <p className="text-sm text-blue-700 mt-1">
                    Please read and fill out the details below carefully.
                  </p>
                </div>

                <div className="flex-grow">
                  {currentPageContent?.sections.map((section, index) =>
                    renderSection(section, index),
                  )}
                </div>
              </fieldset>

              <ApplicationFormFooter
                page={page}
                setPage={setPage}
                totalPages={CONTRACTOR_PAGES.length}
                methods={methods}
                onSave={handleSave}
                onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                isReadOnly={isReadOnly}
                userRole={currentUserData?.roleId || 1}
                isSignatureModalOpen={isSignatureModalOpen}
                openSignatureModal={openSignatureModal}
                closeSignatureModal={closeSignatureModal}
                handleSignatureApply={handleSignatureApply}
                signerName={signerName}
                applicantName={watch("contractorName") || ""}
                applicantSignature={watch("applicantSignature") || ""}
                applicantSignedAt={watch("applicantSignedAt") || ""}
                reviewedBySignedBy={watch("reviewedBySignedBy") || ""}
                reviewedBySignature={watch("reviewedBySignature") || ""}
                reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
                canSignEmployee={currentUserData?.roleId !== 1}
                canSignAdmin={currentUserData?.roleId === 1}
                onStatusUpdate={handleStatusAction}
                onClose={closeForm}
                currentStatus={currentStatus}
              />
            </form>
          </FormProvider>
        </div>
      </div>
    </>
  );
};

export default ContractorServiceAgreement;

ContractorServiceAgreement.propTypes = {
  data: PropTypes.object,
  contractorName: PropTypes.string,
  onClose: PropTypes.func
};
